/** Shared salary structure helpers (Add Employee / Performance Outcomes). */

export const salaryTabFormatDecimal = (value, decimalPlaces = 2) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return parseFloat(Number(value).toFixed(decimalPlaces)).toString();
};

export const salaryTabSanitizeDecimal = (value, decimalPlaces = 2) => {
  if (value === null || value === undefined) return '';
  let sanitized = String(value).replace(/[^0-9.]/g, '');
  const firstDot = sanitized.indexOf('.');
  if (firstDot !== -1) {
    const beforeDot = sanitized.slice(0, firstDot + 1);
    const afterDot = sanitized.slice(firstDot + 1).replace(/\./g, '');
    sanitized = beforeDot + afterDot;
  }
  if (sanitized === '.') return '0.';
  const [intPart = '', decPart] = sanitized.split('.');
  let formatted = intPart;
  if (decPart !== undefined) {
    formatted += `.${decPart.slice(0, decimalPlaces)}`;
  }
  return formatted;
};

export function computeSalaryStructureTotalsFromComponents(components) {
  const list = Array.isArray(components) ? components : [];
  const toNum = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  const totalEarnings = list
    .filter((c) => c.type === 'earning')
    .reduce((s, c) => s + toNum(c.value), 0);
  const totalDeductions = list
    .filter((c) => c.type === 'deduction')
    .reduce((s, c) => s + toNum(c.value), 0);
  const totalReimbursements = list
    .filter((c) => c.type === 'reimbursement')
    .reduce((s, c) => s + toNum(c.value), 0);
  const netPay = totalEarnings + totalReimbursements;
  return { totalEarnings, totalReimbursements, totalDeductions, netPay };
}

export function buildSalaryStructurePayload(monthlyCtcInput, components, justification = '') {
  const n = parseInt(String(monthlyCtcInput), 10);
  if (monthlyCtcInput === '' || !Number.isFinite(n) || n < 1) {
    return null;
  }
  const list = Array.isArray(components) ? components : [];
  const mapped = list.map((c) => ({
    id: String(c.id),
    name: String(c.name),
    type: c.type,
    value: String(c.value ?? '0'),
    percentageValue: String(c.percentageValue ?? '0'),
  }));
  const totals = computeSalaryStructureTotalsFromComponents(mapped);
  return {
    monthlyCtc: String(n),
    components: mapped,
    totalEarnings: totals.totalEarnings,
    totalReimbursements: totals.totalReimbursements,
    totalDeductions: totals.totalDeductions,
    netPay: totals.netPay,
    justification: justification && justification.trim() ? justification.trim() : '',
  };
}

export function mapApiStructureToFormState(currentStructure) {
  if (!currentStructure || currentStructure.monthlyCtc == null || String(currentStructure.monthlyCtc).trim() === '') {
    return { monthlyCtc: '', components: [] };
  }
  return {
    monthlyCtc: String(currentStructure.monthlyCtc),
    components: Array.isArray(currentStructure.components)
      ? currentStructure.components.map((c, index) => ({
          id: String(c.id || `salary-comp-${index}`),
          name: String(c.name || ''),
          type: c.type || 'earning',
          value: String(c.value ?? '0'),
          percentageValue: String(c.percentageValue ?? '0'),
        }))
      : [],
  };
}

export function cloneSalaryComponents(components) {
  return (Array.isArray(components) ? components : []).map((c) => ({
    id: String(c.id),
    name: String(c.name),
    type: c.type,
    value: String(c.value ?? '0'),
    percentageValue: String(c.percentageValue ?? '0'),
  }));
}
